import React, { useEffect, useMemo, useRef, useState } from "react";

import HttpManager from "../../services/HttpManager";
import { SaleContext, SaleProduct, SaleStudent, SaleVariant } from "../../services/v2/sales.service";
import MultiSelect from "./multiSelect";
import KitPaymentPrompt from "./kitPaymentPrompt";
import CamaraVenta, { ScanOutcome } from "./camaraVenta";
import { FIELD_FONT, requestErrorText } from "./kitUi";
import TerceroComprador, { TerceroBuyer, buyerValid, emptyBuyer } from "./terceroComprador";
import { useEsTelefono } from "../../utils/esTelefono";

/*
	La ÚNICA ventana para cargar productos a la cuenta de un alumno (Módulos Claros, 2026-11-01). La abren:
	  - Ventas › «+ Nueva Venta» (from "newSale": primero se busca al alumno; al terminar, Reportar Pago o Nueva Venta);
	  - Estado de Cuenta › «Cargar Productos» (from "accountStatement": el alumno ya viene fijo; al terminar se cierra).
	Mismo cargo para las dos (newSale/_charge; `from` decide el sale_channel: newSale o account). Buscador, lector de código o
	la cámara («Escanear», como la caja del súper: cada código suma una pieza), varios productos de un jalón, Vendido Por, y queda
	en Productos para cobrarlo con el pago de siempre.
	Sin <label>: el CSS global los posiciona absolutos.
*/

interface CartLine { key: string; product: SaleProduct; quantity: number; size: string; variantId: number | null; choices: Record<number, number> }

export interface VentanaVentaProps {
	campusId: number;
	/** Alumno fijo (Estado de Cuenta). Sin él, la ventana empieza buscando al alumno (Nueva Venta). */
	studentId?: number | null;
	currency: string;
	from: "newSale" | "accountStatement";
	/** Bit Crear de Nueva Venta: sin él la ventana solo consulta. */
	canCharge?: boolean;
	onClose: () => void;
	/** Después de cargar. En Estado de Cuenta la ventana se cierra; en Nueva Venta se queda con el resumen. */
	onDone: (total: number, count: number) => void;
}

const variantsOf = (product: SaleProduct): SaleVariant[] => product.variants || [];
const linePrice = (line: CartLine): number => variantsOf(line.product).find((option) => option.id === line.variantId)?.price ?? line.product.price;
/** Las tallas/colores no cuestan todas lo mismo: hay que elegir antes de cargar. */
const mustPick = (line: CartLine): boolean => line.variantId === null && new Set(variantsOf(line.product).map((option) => option.price)).size > 1;

const s = {
	caption: { display: "block", fontSize: "11px", fontWeight: 600, color: "var(--wa-apagado)", marginBottom: "4px", letterSpacing: ".02em" } as React.CSSProperties,
	small: { fontSize: "11px", color: "var(--wa-apagado)", lineHeight: 1.3 } as React.CSSProperties,
	input: { fontSize: FIELD_FONT, backgroundColor: "var(--wa-superficie)", border: "1px solid var(--wa-filo-2)" } as React.CSSProperties,
	step: { width: "28px", height: "28px", padding: 0, lineHeight: 1, borderRadius: "8px" } as React.CSSProperties
};

/** Miniatura del producto (160 px), solo cuando la fila se ve. */
const Foto: React.FC<{ campusId: number; productId: number; has: boolean }> = ({ campusId, productId, has }) => {
	const [url, setUrl] = useState<string | null>(null);
	useEffect(() => {
		if (!has) return;
		let objectUrl: string | null = null;
		let alive = true;
		HttpManager.getInstance().inventoryService.photoBlob(campusId, productId, true)
			.then((response) => { if (!alive) return; objectUrl = URL.createObjectURL(response.data); setUrl(objectUrl); })
			.catch(() => undefined);
		return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
	}, [campusId, productId, has]);
	return <div style={{ width: "42px", height: "42px", flex: "0 0 42px", borderRadius: "8px", background: "var(--wa-papel-2)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
		{url ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
	</div>;
};

const VentanaVenta: React.FC<VentanaVentaProps> = ({ campusId, studentId: fixedStudentId, currency, from, canCharge: allowed = true, onClose, onDone }) => {
	const http = HttpManager.getInstance().salesService;
	// Computadora: buscador y carrito lado a lado, cada uno con su scroll y el total siempre a la vista. Celular: uno debajo del otro.
	const angosta = useEsTelefono(760);
	const money = (value: number) => currency + value.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

	const [studentId, setStudentId] = useState<number | null>(fixedStudentId || null);
	const [studentQuery, setStudentQuery] = useState<string>("");
	const [students, setStudents] = useState<SaleStudent[]>([]);
	const [studentNotice, setStudentNotice] = useState<string | null>(null);
	const [done, setDone] = useState<{ kitIds: number[]; total: number; pending: number } | null>(null);
	const [paying, setPaying] = useState<boolean>(false);
	const [paidNotice, setPaidNotice] = useState<string | null>(null);
	const [context, setContext] = useState<SaleContext | null>(null);
	const [query, setQuery] = useState<string>("");
	const [debounced, setDebounced] = useState<string>("");
	const [results, setResults] = useState<SaleProduct[]>([]);
	const [searching, setSearching] = useState<boolean>(false);
	const [highlight, setHighlight] = useState<number>(-1);
	const [notice, setNotice] = useState<string | null>(null);
	const [cart, setCart] = useState<CartLine[]>([]);
	const [sellerIds, setSellerIds] = useState<string[]>([]);
	const [noSeller, setNoSeller] = useState<boolean>(false);
	const [note, setNote] = useState<string>("");
	const [buyer, setBuyer] = useState<TerceroBuyer>(emptyBuyer());
	const [busy, setBusy] = useState<boolean>(false);
	const [scanning, setScanning] = useState<boolean>(false);
	// La cámara leyó un código que no está en WAPP: al elegir el producto se ofrece guardárselo (la próxima vez ya se reconoce).
	const [unknownCode, setUnknownCode] = useState<string | null>(null);
	const [linkOffer, setLinkOffer] = useState<{ product: SaleProduct; variantId: number | null } | null>(null);
	const [linking, setLinking] = useState<boolean>(false);
	const [linkError, setLinkError] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const searchRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	/** Eligió con las flechas: Enter agrega ese y no busca el texto como código. */
	const navigated = useRef<boolean>(false);

	useEffect(() => {
		if (!studentId) { setContext(null); return; }
		let alive = true;
		http.studentContext(campusId, studentId)
			.then((response) => { if (alive) setContext(response.data); })
			.catch((requestError) => { if (alive) setError(requestErrorText(requestError, "No se pudo abrir al alumno.")); });
		return () => { alive = false; };
	}, [campusId, studentId]);

	// Nueva Venta: buscar al alumno (nombre, teléfono o correlativo).
	useEffect(() => {
		if (studentId) return;
		const text = studentQuery.trim();
		if (text.length < 2) { setStudents([]); setStudentNotice(null); return; }
		let alive = true;
		const handle = setTimeout(() => {
			http.searchStudents(campusId, text)
				.then((response) => { if (alive) { setStudents(response.data); setStudentNotice(response.data.length ? null : "Sin alumnos con esa búsqueda"); } })
				.catch((requestError) => { if (alive) { setStudents([]); setStudentNotice(requestErrorText(requestError, "No se pudo buscar.")); } });
		}, 300);
		return () => { alive = false; clearTimeout(handle); };
	}, [campusId, studentId, studentQuery]);

	/** Otra venta (u otro alumno): todo en blanco, sin cerrar la ventana. */
	const restart = (keepStudent: boolean) => {
		setCart([]); setSellerIds([]); setNoSeller(false); setNote(""); setBuyer(emptyBuyer()); setQuery(""); setResults([]);
		setNotice(null); setError(null); setDone(null); setPaidNotice(null); setPaying(false); forgetCode();
		if (!keepStudent && !fixedStudentId) { setStudentId(null); setContext(null); setStudentQuery(""); setStudents([]); }
		else if (studentId) http.studentContext(campusId, studentId).then((response) => setContext(response.data)).catch(() => undefined);
	};

	useEffect(() => {
		const handle = setTimeout(() => setDebounced(query.trim()), 300);
		return () => clearTimeout(handle);
	}, [query]);

	useEffect(() => {
		if (debounced.length < 2) { setResults([]); setSearching(false); return; }
		let alive = true;
		setSearching(true);
		http.searchProducts(campusId, debounced)
			.then((response) => { if (alive) { setResults(response.data); setHighlight(response.data.length > 0 ? 0 : -1); } })
			.catch((requestError) => { if (alive) { setResults([]); setNotice(requestErrorText(requestError, "No se pudo buscar.")); } })
			.finally(() => { if (alive) setSearching(false); });
		return () => { alive = false; };
	}, [campusId, debounced]);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => { if (event.key === "Escape" && !busy && !paying) onClose(); };
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [busy, paying, onClose]);

	useEffect(() => {
		listRef.current?.querySelector<HTMLElement>(`[data-row="${highlight}"]`)?.scrollIntoView({ block: "nearest" });
	}, [highlight]);

	/** Sin búsqueda se proponen los kits de su curso; con búsqueda, lo que se encontró. */
	const showing: SaleProduct[] = debounced.length >= 2 ? results : (context?.proposed || []);
	const inCart = useMemo(() => {
		const map: Record<number, number> = {};
		cart.forEach((line) => { map[line.product.id] = (map[line.product.id] || 0) + line.quantity; });
		return map;
	}, [cart]);

	/** variantId: la talla/color del código escaneado; cada variante es su propia línea. */
	const add = (product: SaleProduct, variantId: number | null = null) => {
		if (done) return;
		if (product.pricePending) { setNotice(product.name + ": Precio por Aprobar"); return; }
		const scanned = variantsOf(product).find((option) => option.id === variantId) || null;
		setCart((lines) => {
			const index = lines.findIndex((line) => line.product.id === product.id && line.variantId === (scanned ? scanned.id : null));
			if (index >= 0) return lines.map((line, i) => i === index ? { ...line, quantity: line.quantity + 1 } : line);
			return [...lines, { key: product.id + "_" + Date.now() + "_" + lines.length, product, quantity: 1, size: "", variantId: scanned ? scanned.id : null, choices: {} }];
		});
		setNotice(product.name + (scanned ? " · " + scanned.label : "") + " agregado");
		if (unknownCode) { setLinkOffer({ product, variantId: scanned ? scanned.id : null }); setLinkError(null); }
	};
	const forgetCode = () => { setUnknownCode(null); setLinkOffer(null); setLinkError(null); };
	const saveCode = async () => {
		if (!unknownCode || !linkOffer || linking) return;
		const { product, variantId } = linkOffer;
		setLinking(true);
		setLinkError(null);
		try {
			await http.linkCode(campusId, variantId ?? product.id, unknownCode);
			// The size chosen for the code is the size being sold.
			if (variantId !== null) setCart((lines) => lines.map((line) => line.product.id === product.id && line.variantId === null ? { ...line, variantId } : line));
			const label = variantsOf(product).find((option) => option.id === variantId)?.label;
			setNotice("Código guardado en " + product.name + (label ? " · " + label : "") + ": la próxima vez la cámara lo reconoce.");
			forgetCode();
		} catch (requestError) {
			setLinkError(requestErrorText(requestError, "No se pudo guardar el código."));
		} finally {
			setLinking(false);
		}
	};
	/** Lo que leyó la cámara: se suma y se le dice a quien escanea cuántas lleva de ese. */
	const addScanned = (product: SaleProduct, variantId: number | null): ScanOutcome => {
		if (done) return { ok: false, title: "Esta venta ya se cargó", detail: "Toca Nueva Venta para empezar otra." };
		if (product.pricePending) return { ok: false, title: product.name, detail: "Precio por Aprobar: todavía no se puede vender." };
		const scanned = variantsOf(product).find((option) => option.id === variantId) || null;
		const before = cart.find((line) => line.product.id === product.id && line.variantId === (scanned ? scanned.id : null))?.quantity ?? 0;
		add(product, variantId);
		setNotice(null);   // the camera says what went in
		const short = product.tracksStock && (scanned ? scanned.onHand : product.availableHere ?? 0) <= before;
		return { ok: true, title: product.name + (scanned ? " · " + scanned.label : ""),
			detail: (before + 1) + " en la venta · " + money(scanned ? scanned.price : product.price) + (short ? " · Sin existencia aquí" : "") };
	};
	const update = (key: string, change: Partial<CartLine>) => setCart((lines) => lines.map((line) => line.key === key ? { ...line, ...change } : line));
	const remove = (key: string) => setCart((lines) => lines.filter((line) => line.key !== key));

	/**
	 * Enter: lo elegido con las flechas; si no, el código exacto (el lector de código de barras escribe y da Enter antes de que
	 * salga la lista); si no hay código así, el primero de la lista.
	 */
	const onSearchKey = async (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "ArrowDown") { event.preventDefault(); navigated.current = true; setHighlight((h) => Math.min(showing.length - 1, h + 1)); return; }
		if (event.key === "ArrowUp") { event.preventDefault(); navigated.current = true; setHighlight((h) => Math.max(0, h - 1)); return; }
		if (event.key !== "Enter") return;
		event.preventDefault();
		const code = query.trim();
		const listed = code === debounced || (code === "" && debounced === "") ? showing[highlight] : undefined;
		if (listed && (navigated.current || !code)) {
			add(listed);
			return;
		}
		if (!code) return;
		try {
			const exact = await http.searchProducts(campusId, code, true);
			if (exact.data.length === 1) {
				add(exact.data[0], exact.data[0].scannedVariantId ?? null);
				setQuery("");
				return;
			}
		} catch (requestError) {
			// Sin coincidencia exacta se queda con lo resaltado.
		}
		if (listed) {
			add(listed);
		} else {
			setNotice("Ningún producto con el código " + code);
		}
	};

	const total = cart.reduce((sum, line) => sum + line.quantity * linePrice(line), 0);
	const pieces = cart.reduce((sum, line) => sum + line.quantity, 0);
	const thirdParty = !!context?.student.isThirdParty;
	const missing = cart.length < 1 ? null
		: cart.some(mustPick) ? "Falta Talla o Color"
		: !noSeller && sellerIds.length < 1 ? "Falta Vendido Por"
		: thirdParty && !buyerValid(buyer) ? "Faltan Datos del Comprador"
		: null;
	const canCharge = allowed && !!context && cart.length > 0 && !missing && !busy && !done;

	const charge = async () => {
		if (!context || !canCharge) return;
		setBusy(true);
		setError(null);
		try {
			const response = await http.charge(campusId, {
				studentId: context.student.id,
				lines: cart.map((line) => ({
					articleId: line.product.id, quantity: line.quantity, size: line.size.trim() || null, variantId: line.variantId,
					choices: Object.keys(line.choices).map((modelId) => ({ modelId: Number(modelId), variantId: line.choices[Number(modelId)] }))
				})),
				advisors: noSeller ? [] : sellerIds.map((id) => ({ userId: id })),
				note: note.trim(),
				buyer: thirdParty ? { name: buyer.name.trim(), phone: buyer.phone, heardFrom: buyer.heardFrom.trim() } : undefined,
				from
			});
			onDone(response.data.total, pieces);
			if (from === "newSale") {
				setDone(response.data);
				setCart([]);
				setBusy(false);
			}
		} catch (requestError) {
			setError(requestErrorText(requestError, "No se pudo cargar."));
			setBusy(false);
		}
	};

	const stock = (product: SaleProduct) => {
		if (!product.tracksStock) return <span>{product.legacy ? "Catálogo viejo" : product.kindLabel}</span>;
		const here = product.availableHere ?? 0;
		return <>
			<span style={{ color: here > 0 ? "var(--wa-tinta)" : "var(--wa-oro)", fontWeight: 700 }}>{here > 0 ? "Aquí " + here : "Sin Existencia Aquí"}</span>
			{product.elsewhere.length > 0 ? <span>{product.elsewhere.map((row) => row.code + " " + row.onHand).join(" · ")}</span> : null}
			{product.inTransit > 0 ? <span>En camino {product.inTransit}{product.eta ? " · " + product.eta : ""}</span> : null}
		</>;
	};

	const sellerOptions = (context?.sellers || []).map((seller) => ({
		id: seller.id, name: seller.name,
		hint: seller.kind === "teacher" ? (seller.isStudentTeacher ? "Maestra del Alumno" : "Maestra") : undefined
	}));

	return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2100, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
		onMouseDown={(event) => { if (event.target === event.currentTarget && !busy && !paying) onClose(); }}>
		<div role="dialog" aria-modal="true" aria-label="Cargar Productos"
			style={{ background: "var(--wa-superficie)", color: "var(--wa-tinta)", borderRadius: "14px", width: "min(1040px, 100%)", height: "min(720px, 100%)", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 18px 50px rgba(43,28,22,.28)" }}>

			<div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px 18px", borderBottom: "1px solid var(--wa-filo)" }}>
				<div style={{ flex: 1, minWidth: 0 }}>
					<div style={{ fontSize: "20px", fontWeight: 700 }}>{from === "newSale" ? "Nueva Venta" : "Cargar Productos"}</div>
					<div style={{ ...s.small, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
						{context ? [context.student.name, context.student.identityCode, ...context.enrollments.slice(0, 1).map((e) => [e.course, e.days, e.hours, e.mentor].filter(Boolean).join(" "))].filter(Boolean).join(", ") : studentId ? "…" : ""}</div>
				</div>
				{!fixedStudentId && studentId && !busy ? <button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" onClick={() => restart(false)}>Cambiar Alumno</button> : null}
				<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={busy} onClick={onClose} aria-label="Cerrar">✕</button>
			</div>

			{!studentId ? <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "14px 18px" }}>
				<input className="form-control" autoFocus value={studentQuery} aria-label="Alumno"
					style={{ ...s.input, height: "42px", fontSize: FIELD_FONT === "16px" ? "16px" : "15px", borderRadius: "10px" }}
					placeholder="Alumno: nombre, teléfono o correlativo" onChange={(event) => setStudentQuery(event.target.value)} />
				<div style={{ ...s.small, minHeight: "16px", marginTop: "4px" }}>{studentNotice || ""}</div>
				{students.map((student) => <div key={student.id} role="button" tabIndex={0} onClick={() => setStudentId(student.id)}
					onKeyDown={(event) => { if (event.key === "Enter") setStudentId(student.id); }}
					style={{ padding: "10px", borderRadius: "10px", cursor: "pointer", borderBottom: "1px solid var(--wa-filo)", opacity: student.statusId === 1 ? 1 : 0.6 }}>
					<b>{student.name}</b> <span style={s.small}>{[student.identityCode, student.phone].filter(Boolean).join(", ")}</span>
					{student.courses.length > 0 ? <div style={s.small}>{student.courses.join(", ")}</div> : null}
				</div>)}
			</div> : <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: angosta ? "column" : "row", overflowY: angosta ? "auto" : "hidden" }}>
				{/* Buscador y resultados */}
				<div style={{ flex: angosta ? "0 0 auto" : "1 1 58%", minWidth: 0, minHeight: angosta && !scanning ? "320px" : 0, maxHeight: angosta && !scanning ? "45vh" : undefined, display: "flex", flexDirection: "column",
					borderRight: angosta ? undefined : "1px solid var(--wa-filo)", borderBottom: angosta ? "1px solid var(--wa-filo)" : undefined }}>
					<div style={{ padding: "12px 16px 8px" }}>
						<div style={{ display: "flex", gap: "8px" }}>
							<input ref={searchRef} className="form-control" autoFocus={!scanning} value={query} disabled={busy}
								style={{ ...s.input, flex: 1, minWidth: 0, height: "42px", fontSize: FIELD_FONT === "16px" ? "16px" : "15px", borderRadius: "10px" }}
								placeholder="Producto: nombre o código" aria-label="Buscar producto"
								onChange={(event) => { setQuery(event.target.value); setNotice(null); navigated.current = false; }} onKeyDown={onSearchKey} />
							{!scanning ? <button type="button" className="btn btn-primary rounded-pill" style={{ flex: "0 0 auto", height: "42px", padding: "0 16px", fontWeight: 600 }}
								disabled={busy || !!done} onClick={() => { forgetCode(); setScanning(true); }}>Escanear</button> : null}
						</div>
						<div style={{ ...s.small, minHeight: "16px", marginTop: "4px" }}>
							{notice || (searching ? "Buscando…" : debounced.length >= 2 && results.length < 1 ? "Sin productos con esa búsqueda"
								: debounced.length < 2 && showing.length > 0 ? "Kit del Curso" : "")}
						</div>
						{unknownCode && !done ? <div role="status" style={{ marginTop: "6px", padding: "8px 10px", borderRadius: "10px", border: "1px solid var(--wa-oro)", background: "var(--wa-oro-suave)", color: "var(--wa-tinta)", fontSize: "13px", lineHeight: 1.35 }}>
							{!linkOffer ? <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
								<div style={{ flex: 1, minWidth: 0 }}>El código <b style={{ fontFamily: "monospace" }}>{unknownCode}</b> no está en WAPP. Busca el producto y tócalo para guardarle el código.</div>
								<button type="button" className="btn btn-link btn-sm" style={{ padding: "0 2px", lineHeight: 1.2, color: "var(--wa-apagado)", textDecoration: "none" }} onClick={forgetCode} aria-label="No guardar el código">✕</button>
							</div> : <>
								<div>¿Guardar el código <b style={{ fontFamily: "monospace" }}>{unknownCode}</b> en <b>{linkOffer.product.name}</b>?</div>
								{variantsOf(linkOffer.product).length > 0 ? <select className="form-select form-select-sm" value={linkOffer.variantId ?? ""} disabled={linking}
									style={{ ...s.input, marginTop: "6px", borderColor: linkOffer.variantId === null ? "var(--wa-oro)" : undefined }}
									onChange={(event) => setLinkOffer({ ...linkOffer, variantId: event.target.value === "" ? null : Number(event.target.value) })}>
									<option value="">Elige la talla o color del código</option>
									{variantsOf(linkOffer.product).map((option) => <option key={option.id} value={option.id} disabled={!!option.barcode}>
										{option.label}{option.barcode ? " · ya tiene código" : ""}</option>)}
								</select> : null}
								{linkError ? <div style={{ color: "var(--wa-rojo-error)", marginTop: "6px" }}>{linkError}</div> : null}
								<div style={{ display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
									<button type="button" className="btn btn-primary btn-sm rounded-pill" style={{ minHeight: "34px", padding: "0 14px" }}
										disabled={linking || (variantsOf(linkOffer.product).length > 0 && linkOffer.variantId === null)} onClick={saveCode}>{linking ? "Guardando…" : "Guardar Código"}</button>
									<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" style={{ minHeight: "34px", padding: "0 14px" }}
										disabled={linking} onClick={forgetCode}>No Guardar</button>
								</div>
							</>}
						</div> : null}
					</div>
					{scanning && !done ? <CamaraVenta campusId={campusId} enabled={!busy && !paying} compact={angosta}
						onProduct={addScanned} onClose={() => setScanning(false)}
						onNotFound={(code) => { setScanning(false); if (allowed) { setUnknownCode(code); setLinkOffer(null); setLinkError(null); } setTimeout(() => searchRef.current?.focus(), 0); }} /> : null}
					{/* Celular con la cámara prendida: la lista solo sale si escriben algo; el carrito queda justo abajo. */}
					<div ref={listRef} style={{ flex: 1, overflowY: "auto", padding: "0 8px 12px", display: scanning && !done && angosta && debounced.length < 2 ? "none" : undefined }}>
						{showing.map((product, index) => {
							const count = inCart[product.id] || 0;
							const active = index === highlight;
							return <div key={product.id} data-row={index} onMouseEnter={() => setHighlight(index)}
								onClick={() => !busy && add(product)}
								style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px", borderRadius: "10px", cursor: product.pricePending ? "default" : "pointer",
									background: active ? "var(--wa-papel-2)" : "transparent", opacity: product.pricePending ? 0.55 : 1 }}>
								<Foto campusId={campusId} productId={product.id} has={product.hasPhoto} />
								<div style={{ flex: 1, minWidth: 0 }}>
									<div style={{ fontWeight: 700, fontSize: "14px", lineHeight: 1.25 }}>{product.name}
										{product.required ? <span style={{ ...s.small, marginLeft: "6px", color: "var(--wa-acento-texto)", fontWeight: 700 }}>Obligatorio</span> : null}
										{product.alreadyBought ? <span style={{ ...s.small, marginLeft: "6px" }}>Ya lo Compró</span> : product.alreadyCharged ? <span style={{ ...s.small, marginLeft: "6px" }}>Cargado Sin Pago</span> : null}
									</div>
									<div style={{ ...s.small, display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "2px" }}>
										<span>{product.waCode || product.sku}</span>{stock(product)}
									</div>
								</div>
								<div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
									{product.pricePending ? <span style={{ ...s.small, color: "var(--wa-oro)" }}>Sin precio</span> : <b style={{ fontSize: "14px" }}>{money(product.price)}</b>}
									{!product.pricePending && variantsOf(product).some((option) => option.price > product.price)
										? <div style={s.small}>hasta {money(Math.max(...variantsOf(product).map((option) => option.price)))}</div> : null}
								</div>
								<button type="button" className={count > 0 ? "btn btn-primary btn-sm rounded-pill" : "btn btn-outline-secondary btn-sm rounded-pill"}
									style={{ minWidth: "64px" }} disabled={busy || product.pricePending || !!done}
									onClick={(event) => { event.stopPropagation(); add(product); }}>{count > 0 ? "+1 · " + count : "Agregar"}</button>
							</div>;
						})}
					</div>
				</div>

				{/* Lo que se va a cargar */}
				<div style={{ flex: angosta ? "0 0 auto" : "1 1 42%", minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", background: "var(--wa-papel)" }}>
					<div style={{ flex: 1, minHeight: 0, overflowY: angosta ? "visible" : "auto", padding: "12px 16px" }}>
						<span style={s.caption}>Por Cargar{pieces > 0 ? " · " + pieces + (pieces === 1 ? " pieza" : " piezas") : ""}</span>
						{cart.length < 1 ? <div style={{ ...s.small, padding: "24px 0", textAlign: "center" }}>Sin productos</div> : cart.map((line) => <div key={line.key}
							style={{ padding: "10px 0", borderBottom: "1px solid var(--wa-filo)" }}>
							<div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
								<b style={{ flex: 1, minWidth: 0, fontSize: "13px", lineHeight: 1.3 }}>{line.product.name}</b>
								<span style={{ whiteSpace: "nowrap", fontWeight: 700, fontSize: "13px" }}>{money(line.quantity * linePrice(line))}</span>
								<button type="button" className="btn btn-link btn-sm" style={{ padding: "0 2px", lineHeight: 1, color: "var(--wa-apagado)", textDecoration: "none" }}
									disabled={busy} onClick={() => remove(line.key)} aria-label={"Quitar " + line.product.name}>✕</button>
							</div>
							<div style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: "6px", flexWrap: "wrap" }}>
								<button type="button" className="btn btn-outline-secondary btn-sm" style={s.step} disabled={busy}
									onClick={() => line.quantity <= 1 ? remove(line.key) : update(line.key, { quantity: line.quantity - 1 })} aria-label="Una menos">−</button>
								<input className="form-control" inputMode="numeric" value={line.quantity} disabled={busy} aria-label="Cantidad"
									style={{ ...s.input, width: "48px", height: "28px", padding: "0 4px", textAlign: "center" }}
									onChange={(event) => { const n = parseInt(event.target.value.replace(/\D/g, ""), 10); update(line.key, { quantity: Math.min(500, Math.max(1, isNaN(n) ? 1 : n)) }); }} />
								<button type="button" className="btn btn-outline-secondary btn-sm" style={s.step} disabled={busy}
									onClick={() => update(line.key, { quantity: Math.min(500, line.quantity + 1) })} aria-label="Una más">+</button>
								<span style={s.small}>× {money(linePrice(line))}</span>
								{variantsOf(line.product).length > 0 ? <select className="form-select form-select-sm" value={line.variantId ?? ""} disabled={busy}
									style={{ ...s.input, width: "auto", maxWidth: "100%", marginLeft: "auto", borderColor: mustPick(line) ? "var(--wa-oro)" : undefined }}
									onChange={(event) => update(line.key, { variantId: event.target.value === "" ? null : Number(event.target.value) })}>
									<option value="">{mustPick(line) ? "Elige talla / color" : "Talla / color al entregar"}</option>
									{variantsOf(line.product).map((option) => <option key={option.id} value={option.id}>
										{option.label}{Math.abs(option.price - line.product.price) >= 0.005 ? " · " + money(option.price) : ""}{line.product.tracksStock ? " · aquí " + option.onHand : ""}</option>)}
								</select> : (line.product.kind === "uniform" || line.product.kind === "kit") && !(line.product.kitChoices || []).length
									? <input className="form-control form-control-sm" value={line.size} placeholder="Talla" disabled={busy}
										style={{ ...s.input, width: "100px", marginLeft: "auto" }} onChange={(event) => update(line.key, { size: event.target.value })} /> : null}
							</div>
							{(line.product.kitChoices || []).map((piece) => <div key={piece.modelId} style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: "5px" }}>
								<span style={{ ...s.small, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={piece.name}>{piece.name}{piece.quantity > 1 ? " ×" + piece.quantity : ""}</span>
								<select className="form-select form-select-sm" value={line.choices[piece.modelId] ?? ""} disabled={busy} style={{ ...s.input, width: "170px", flex: "0 0 auto" }}
									onChange={(event) => {
										const choices = { ...line.choices };
										if (event.target.value === "") delete choices[piece.modelId]; else choices[piece.modelId] = Number(event.target.value);
										update(line.key, { choices });
									}}>
									<option value="">Al entregar</option>
									{piece.variants.map((option) => <option key={option.id} value={option.id}>{option.label} · aquí {option.onHand}</option>)}
								</select>
							</div>)}
						</div>)}

						{cart.length > 0 && context ? <div style={{ marginTop: "14px" }}>
							<MultiSelect caption="Vendido Por" width="100%" options={sellerOptions} value={sellerIds} disabled={busy || noSeller} allLabel="Elegir…" searchable
								onChange={(ids: string[]) => { setSellerIds(ids); if (ids.length > 0) setNoSeller(false); }} />
							<div onClick={() => { if (!busy) { const next = !noSeller; setNoSeller(next); if (next) setSellerIds([]); } }}
								style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "6px", cursor: "pointer", fontSize: "13px" }}>
								<input type="checkbox" checked={noSeller} readOnly disabled={busy} /> Sin Vendedor
							</div>
							<span style={{ ...s.caption, marginTop: "12px" }}>Nota</span>
							<textarea className="form-control" rows={2} value={note} disabled={busy} style={{ ...s.input, resize: "vertical" }} onChange={(event) => setNote(event.target.value)} />
							{thirdParty ? <TerceroComprador campusId={campusId} value={buyer} onChange={setBuyer} disabled={busy} /> : null}
						</div> : null}
					</div>

					<div style={{ padding: "12px 16px 14px", borderTop: "1px solid var(--wa-filo)", background: "var(--wa-superficie)", position: angosta ? "sticky" : undefined, bottom: 0 }}>
						{error ? <div className="alert alert-danger" style={{ padding: "6px 10px", fontSize: "13px", marginBottom: "8px" }}>{error}</div> : null}
						{done ? <>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "6px" }}>
								<span style={{ fontWeight: 700 }}>Cargado a la Cuenta</span>
								<span style={{ fontSize: "22px", fontWeight: 700 }}>{money(done.total)}</span>
							</div>
							{done.pending > done.total ? <div style={{ ...s.small, color: "var(--wa-oro)", marginBottom: "6px" }}>Por Cobrar {money(done.pending)}</div> : null}
							{paidNotice ? <div style={{ ...s.small, marginBottom: "6px" }}>{paidNotice}</div> : null}
							<div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
								{!paidNotice && allowed ? <button type="button" className="btn btn-outline-secondary rounded-pill" style={{ flex: "1 1 auto", height: "40px" }} onClick={() => setPaying(true)}>Reportar Pago</button> : null}
								<button type="button" className="btn btn-primary rounded-pill" style={{ flex: "1 1 auto", height: "40px" }} onClick={() => restart(false)}>Nueva Venta</button>
							</div>
						</> : <>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "8px" }}>
							<span style={{ fontWeight: 700 }}>Total</span>
							<span style={{ fontSize: "22px", fontWeight: 700 }}>{money(total)}</span>
						</div>
						{missing ? <div style={{ ...s.small, color: "var(--wa-oro)", marginBottom: "6px" }}>{missing}</div> : null}
						<button type="button" className="btn btn-primary rounded-pill" style={{ width: "100%", height: "40px" }} disabled={!canCharge} onClick={charge}>
							{busy ? "Cargando…" : "Cargar a la Cuenta"}</button>
						</>}
					</div>
				</div>
			</div>}
		</div>
		{paying && done && context ? <KitPaymentPrompt campusId={campusId} kitIds={done.kitIds} studentName={context.student.name} pendingAmount={done.pending}
			section="newSale" onDone={(intentId: number, message: string) => { setPaying(false); setPaidNotice(message); }} onCancel={() => setPaying(false)} /> : null}
	</div>;
};

export default VentanaVenta;
